import axios from "axios";
import { User, UserManager } from "oidc-client-ts";
import { toast } from "react-toastify";

import * as Sentry from "@sentry/browser";

import { oidcConfig } from "../components/auth";
import { routes } from "../constants";
import { isTech } from "../utils";
import { normaliseEndpoint } from "../utils/sentry";

// Create UserManager for token refresh
let userManager = null;

const getUserManager = () => {
	if (!userManager) {
		userManager = new UserManager(oidcConfig);
	}
	return userManager;
};

// Track ongoing refresh to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise = null;

export const getUser = () => {
	const oidcStorage = sessionStorage.getItem(
		`oidc.user:${
			isTech
				? import.meta.env.VITE_STS_AUTHORITY_TECH
				: import.meta.env.VITE_STS_AUTHORITY
		}:${import.meta.env.VITE_CLIENT_ID}`,
	);
	if (!oidcStorage) {
		return null;
	}

	return User.fromStorageString(oidcStorage);
};

// Check if token is expired or will expire soon (within 5 minutes)
const isTokenExpired = (user) => {
	if (!user || !user.expires_at) {
		return true;
	}

	const now = Math.floor(Date.now() / 1000);
	const buffer = 60; // 1 minutes buffer
	return user.expires_at <= now + buffer;
};

// Helper function to refresh token
const refreshTokenIfNeeded = async () => {
	if (isRefreshing && refreshPromise) {
		return refreshPromise;
	}

	isRefreshing = true;
	refreshPromise = (async () => {
		try {
			console.log("Refreshing token...");
			const user = await getUserManager().signinSilent();
			console.log("Token refreshed successfully");

			return user;
		} catch (error) {
			console.error("Token refresh failed:", error);
			await getUserManager().removeUser();
			window.location.href = "/";
			return null;
		} finally {
			isRefreshing = false;
			refreshPromise = null;
		}
	})();

	return refreshPromise;
};

// Get valid user token, refreshing if necessary
const getValidUser = async () => {
	let user = getUser();

	if (!user) {
		return null;
	}

	if (isTokenExpired(user)) {
		user = await refreshTokenIfNeeded();
	}

	return user;
};

// Enhanced axios instance creator
const createAxiosInstance = (headers, baseURL, options = {}) => {
	const instance = axios.create({
		headers,
		baseURL,
	});

	if (options.isFile) {
		instance.defaults.responseType = "blob";
	}

	// Add response interceptor for 401 handling (backup)
	instance.interceptors.response.use(
		(response) => response,
		async (error) => {
			const originalRequest = error.config;

			if (
				error.response?.status === 401 &&
				!originalRequest._retry &&
				!options.isAnonymous
			) {
				originalRequest._retry = true;
				try {
					const refreshedUser = await refreshTokenIfNeeded();

					if (refreshedUser?.access_token) {
						// Update the authorization header and retry
						originalRequest.headers.Authorization = `Bearer ${refreshedUser.access_token}`;
						return instance.request(originalRequest);
					}
				} catch (refreshError) {
					console.error(
						"Failed to refresh token and retry request:",
						refreshError,
					);
				}
			}

			// For non-401 errors or failed retries, continue with original error handling
			return Promise.reject(error);
		},
	);

	return instance;
};

export const getId = (endpoint, options = {}) =>
	get(
		endpoint,
		options,
		(isTech
			? import.meta.env.VITE_STS_AUTHORITY_TECH
			: import.meta.env.VITE_STS_AUTHORITY) + "/api/v1",
	);

export const getBlast = (endpoint, options = {}) =>
	get(
		endpoint,
		options,
		isTech
			? import.meta.env.VITE_BLAST_API_URL_TECH
			: import.meta.env.VITE_BLAST_API_URL,
	);

const _blastV2BaseUrl = (url) =>
	(url || "").replace(/\/api\/v1\/?$/, "/api/v2");

export const getBlastV2 = (endpoint, options = {}) =>
	get(
		endpoint,
		options,
		_blastV2BaseUrl(
			isTech
				? import.meta.env.VITE_BLAST_API_URL_TECH
				: import.meta.env.VITE_BLAST_API_URL,
		),
	);

export const postBlastV2 = (endpoint, payload = {}, options = {}) =>
	post(
		endpoint,
		payload,
		options,
		_blastV2BaseUrl(
			isTech
				? import.meta.env.VITE_BLAST_API_URL_TECH
				: import.meta.env.VITE_BLAST_API_URL,
		),
	);

export const get = async (
	endpoint,
	options = {},
	api = isTech
		? import.meta.env.VITE_API_URL_TECH
		: import.meta.env.VITE_API_URL,
) => {
	return new Promise(async (resolve, reject) => {
		let headers = {};

		if (!options.isAnonymous) {
			// Get valid user (refresh if needed)
			const user = await getValidUser();

			if (user?.access_token) {
				headers["Authorization"] = `Bearer ${user.access_token}`;
			} else if (!options.allowUnauthenticated) {
				// No valid token available
				reject(new Error("No valid authentication token available"));
				return;
			}
		}

		let instance = createAxiosInstance(headers, api, options);

		return instance
			.get(endpoint)
			.then((result) => {
				handleSuccess(result, options, endpoint, "GET");
				resolve(result.data?.data || result?.data || result);
			})
			.catch((error) => {
				let errorMessage = handleError(error, options, endpoint, "GET");

				if (options.handleErrorManually) {
					resolve({ inError: true, message: errorMessage });
				} else {
					reject(error);
				}
			});
	});
};

export const postId = (endpoint, payload = {}, options = {}) =>
	post(
		endpoint,
		payload,
		options,
		(isTech
			? import.meta.env.VITE_STS_AUTHORITY_TECH
			: import.meta.env.VITE_STS_AUTHORITY) + "/api/v1",
	);

export const postBlast = (endpoint, payload = {}, options = {}) =>
	post(
		endpoint,
		payload,
		options,
		isTech
			? import.meta.env.VITE_BLAST_API_URL_TECH
			: import.meta.env.VITE_BLAST_API_URL,
	);

export const post = async (endpoint, payload = {}, options = {}, api) => {
	const innerPost = async () =>
		new Promise(async (resolve, reject) => {
			let headers = {};

			if (!options.isAnonymous) {
				// Get valid user (refresh if needed)
				const user = await getValidUser();

				if (user?.access_token) {
					headers["Authorization"] = `Bearer ${user.access_token}`;
				} else if (!options.allowUnauthenticated) {
					reject(
						new Error("No valid authentication token available"),
					);
					return;
				}
			}

			let instance = createAxiosInstance(headers, api, options);

			return instance
				.post(endpoint, payload)
				.then((result) => {
					handleSuccess(result, options, endpoint, "POST", payload);
					resolve(result.data?.data || result?.data || result);
				})
				.catch((error) => {
					let errorMessage = handleError(
						error,
						options,
						endpoint,
						"POST",
						payload,
					);

					if (options.handleErrorManually) {
						resolve({ inError: true, message: errorMessage });
					} else {
						reject(error);
					}
				});
		});

	// let resp =
	// const maxRuns = 5;
	// let runs = 0;
	// while (
	// 	runs < maxRuns &&
	// 	resp?.fetchedCount !== undefined &&
	// 	resp?.totalAvailable !== undefined &&
	// 	resp?.skippedCount !== undefined &&
	// 	resp.fetchedCount + resp.skippedCount !== resp.totalAvailable
	// ) {
	// 	runs++;
	// 	payload.skip = resp?.fetchedCount + resp?.skippedCount;
	// 	const resp2 = await innerPost();
	// 	resp2.results = [...resp.results, ...resp2.results];
	// 	resp = resp2;
	// }

	return await innerPost();
};

function handleSuccess(result, options, endpoint, httpMethod) {
	if (result?.data?.success === false) {
		// A 200 response carrying an application-level failure. Throw a real
		// Error so it flows through the normal .catch -> handleError path (a
		// single, useful capture) instead of reporting a stray statusless
		// event here and throwing `Error(result)`, which stringified to the
		// useless "[object Object]".
		const error = new Error(
			`API request failed (success=false): ${httpMethod} ${endpoint}`,
		);
		error.response = { status: 200, data: { errors: result.data.errors } };
		throw error;
	}

	if (options?.successMessage) toast.success(options.successMessage);
}

function handleError(error, options, endpoint, httpMethod, payload) {
	let errorMessage = <></>;
	let errorResponse = error.response && error.response.data;

	payload = httpMethod === "GET" ? endpoint.split("?")[1] : payload;

	endpoint = endpoint.split("?")[0];
	let errorsStrings = [];

	if (!errorResponse || typeof errorResponse !== "string") {
		if (errorResponse?.errors) {
			for (const errorKey of Object.keys(errorResponse?.errors)) {
				const message =
					errorResponse.errors[errorKey]?.message ||
					errorResponse.errors[errorKey];

				const errorCode =
					errorResponse.errors[errorKey]?.errorCode || errorKey;

				errorsStrings.push(<br />);
				errorsStrings.push(
					<>{errorCode + (message ? ": " + message : "")}</>,
				);
			}
		}
	} else if (typeof errorResponse === "string") {
		errorsStrings.push(<>{errorResponse}</>);
	}

	if (errorResponse?.error) {
		errorsStrings.push(<br />);
		errorsStrings.push(
			<>
				{errorResponse.error +
					(errorResponse?.message
						? ": " + errorResponse.message
						: "")}
			</>,
		);
	}

	if (error?.response?.status === 403) {
		errorsStrings.push(<br />);
		errorsStrings.push(
			<>
				403 Forbidden. You do not have permission to access this
				resource: {endpoint}
			</>,
		);
	}

	errorMessage = <>Something's gone wrong!{errorsStrings}</>;

	if (error?.response) {
		switch (error.response.status) {
			case 404: {
				if (!options.handleErrorManually && errorMessage)
					toast.error(errorMessage, {
						autoClose: false,
						toastId: "Resource not found",
					});

				break;
			}
			case 401: {
				if (!options.handleErrorManually && errorMessage)
					toast.error("Session expired, refreshing page.", {
						autoClose: false,
						toastId: "Unauthorised",
					});

				if (window.location.pathname !== routes.notAuthenticated.path)
					window.location.pathname = routes.notAuthenticated.path;

				break;
			}
			case 503: {
				// if (!options.handleErrorManually && errorResponse?.message)
				// 	toast.error(errorMessage, {
				// 		autoClose: false,
				// 		toastId: "Maintenance",
				// 	});

				if (window.location.pathname !== routes.maintenance.path)
					window.location.pathname = routes.maintenance.path;
				break;
			}
			case 400:
			case 500:
			default: {
				// Normalise ids out of the path so Sentry groups by route
				// template (e.g. /organisations/:id) instead of one issue per
				// id. Keep the real endpoint in tags/contexts for debugging.
				const route = normaliseEndpoint(endpoint);
				error.name = `${error.name}: ${error.response.status} - ${httpMethod} ${route}`;

				const sentryContext = {
					fingerprint: [route, error.response.status],
					tags: {
						endpoint,
						route,
						status: error.response.status,
						method: httpMethod,
					},
					contexts: {
						api_request: {
							endpoint,
							method: httpMethod,
							status_code: error.response.status,
							...(payload && {
								payload,
								payload_string: JSON.stringify(
									payload,
									null,
									2,
								),
							}),
						},
						api_response: {
							// Include response data
							response_data: errorResponse,
							response_data_string: JSON.stringify(
								errorResponse,
								null,
								2,
							),
							response_headers: error.response.headers,
						},
					},
				};

				Sentry.captureException(error, sentryContext);
				if (!options.handleErrorManually && errorMessage)
					toast.error(errorMessage, {
						autoClose: false,
						toastId: errorMessage.props.children[0],
					});
				break;
			}
		}
	}

	return errorMessage;
}

export function isMockMode() {
	return (
		(import.meta.env.VITE_MOCK_MODE === "true" && import.meta.env.DEV) ||
		import.meta.env.MODE === "test"
	);
}

export function simulateDelay(delay, func) {
	if (import.meta.env.MODE === "test")
		return new Promise((resolve) => resolve(func()));

	return new Promise(function (resolve) {
		setTimeout(function () {
			resolve(func());
		}, delay);
	});
}

export const debugUtils = {
	// Force expire the current token for testing
	expireToken: () => {
		const user = getUser();
		if (user) {
			// Set expiration to past date
			user.expires_at = Math.floor(Date.now() / 1000) - 3600; // 1 hour ago

			// Update session storage with expired token
			const key = `oidc.user:${
				isTech
					? import.meta.env.VITE_STS_AUTHORITY_TECH
					: import.meta.env.VITE_STS_AUTHORITY
			}:${import.meta.env.VITE_CLIENT_ID}`;

			sessionStorage.setItem(key, user.toStorageString());
			console.log("� Token artificially expired for testing");
		}
	},

	// Check current token status
	checkTokenStatus: () => {
		const user = getUser();
		if (!user) {
			console.log("❌ No user found");
			return;
		}

		const now = Math.floor(Date.now() / 1000);
		const expiresAt = user.expires_at;
		const isExpired = isTokenExpired(user);

		console.log(`Token Status:
      - Expires at: ${new Date(expiresAt * 1000)}
      - Current time: ${new Date()}
      - Is expired: ${isExpired}
      - Time remaining: ${
			isExpired ? "EXPIRED" : Math.floor((expiresAt - now) / 60)
		} minutes`);
	},
};
